<template>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
  <ClientOnly>
    <UiToastLayer :class="$style.toastLayer" />
    <UiModalLayer :class="$style.modalLayer" />
  </ClientOnly>
</template>

<script setup lang="ts">

const i18nHead = useLocaleHead({
  seo: true,
});

useHead(() => ({
  htmlAttrs: {
    ...(i18nHead.value.htmlAttrs ?? {}),
  },
  link: [
    ...(i18nHead.value.link ?? []),
    {
      rel: 'preload',
      as: 'font',
      type: 'font/woff',
      href: '/fonts/Gilroy-Regular.woff',
      crossorigin: 'anonymous',
    },
  ],
  meta: [...(i18nHead.value.meta ?? [])],
  script: [{ src: '/js/aos.js', defer: true }],
}));

const { user } = storeToRefs(useAuthStore());

if (import.meta.client) {
  onMounted(() => {
    window?.visualViewport?.addEventListener('resize', getVisualViewportHeight);
    window.addEventListener('orientationchange', getVisualViewportHeight);
    getVisualViewportHeight();

    const initAos = () => {
      AOS.init({
        once: true,
        disable: () => window.matchMedia('(max-width: 1023px)').matches,
      });
    };
    initAos();
    window.matchMedia('(min-width: 1024px)').addEventListener('change', () => {
      AOS.refresh();
    });

    if (window?.$crisp && user.value?.email) {
      window.$crisp.push(['set', 'user:email', user.value.email]);
    }
  });

  function getVisualViewportHeight() {
    const doc = document.documentElement;
    doc.style.setProperty('--visual-viewport-height', `${window?.visualViewport?.height}px`);
  }

  watch(user, () => {
    if (window?.$crisp && user.value?.email) {
      window.$crisp.push(['set', 'user:email', user.value.email]);
    }
  }, { deep: true });
}
</script>

<style lang="scss">
@font-face {
  font-family: 'Gilroy';
  src: url('/fonts/Gilroy-Regular.woff') format('woff');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@import url('~/assets/styles/styles.scss');
@import url('~/assets/styles/aos.css');

/* On mobile, AOS is disabled in JS, but aos.css hides [data-aos] (opacity:0) before init—a white screen before hydration. Showing content immediately. */
@media (max-width: 1023px) {
  [data-aos] {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
}

html {
  font-family: 'Gilroy';
  font-weight: 300;
  color: #596269;
  scroll-behavior: smooth;

  @media (min-width: 1024px) {
    font-size: 16px;
    line-height: 1.4;
  }

  @media (max-width: 1023px) {
    font-size: 14px;
    line-height: normal;
  }
}

/* swiper/swiper-bundle.css removed — loaded per-component */

.cs .swiper-horizontal > .swiper-scrollbar,
.cs .swiper-scrollbar.swiper-scrollbar-horizontal {
  max-width: 694px;
  width: 100%;
  left: 50%;
  height: 4px;
  transform: translateX(-50%);
  border-radius: 2px;
  background: #D9D9D9;

  @media (max-width: 1023px) {
    max-width: 454px;
  }
}

.cs .swiper-scrollbar-drag {
  height: 4px;
  border-radius: 2px;
  background: #293EFF;
}
</style>

<style lang="scss" module>
.toastLayer {
  position: fixed;
  inset: 0;
  z-index: 100;
}

.modalLayer {
  position: fixed;
  inset: 0;
  z-index: 99;
}
</style>